import React, { useMemo } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";

import { NumberStyler } from "../../../../helper/NumberStyler";
import { getWalletName } from "../../../../helper/wallet/getWalletName";
import { useValueHoldings } from "../../../../store/useValueHoldings";
import { ImageWithMissingSrc } from "../../../atoms/ImageWithMissingSrc";
import { RenderIcons } from "../../../atoms/RenderIcons";
import { sortAssetHoldings } from "../../../../helper/wallet/sortAssetHoldings";
import { SkeletonRect } from "../../../atoms/Skeletons";
import { getDataSourceStatusProps } from "../getDataSourceStatusProps";
import { LastSyncedTime } from "../LastSyncedTime";
import { DUMMY_HOLDINGS } from "../dummyData";
import { DataSourceWalletResponse } from "./DataSourceTable";

export const DataSourceTableRow = ({
  walletResponse,
  isLoading,
}: {
  walletResponse: DataSourceWalletResponse;
  isLoading?: boolean;
}): JSX.Element => {
  const history = useHistory();

  const statusProps = getDataSourceStatusProps(walletResponse);
  const { data: holdingsValue, isLoading: marketValueLoading } =
    useValueHoldings();
  const walletValue = useMemo(
    () =>
      holdingsValue?.getWalletTotals({ walletId: walletResponse._id })
        ?.marketValue,
    [holdingsValue, walletResponse._id]
  );

  return (
    <Flex
      w="100%"
      justifyContent="space-between"
      alignItems="center"
      borderWidth="0 0 1px 0"
      borderColor="white.500"
      h="70px"
      p="8px 16px"
    >
      {/* ------------ Wallet column -----------------*/}
      <Flex w="30%" alignItems="center" minW="250px">
        <ImageWithMissingSrc
          src={walletResponse.dataSource.image}
          alt={walletResponse.dataSource.name}
          mr="10px"
          w="40px"
          minW="40px"
          isLoading={isLoading}
          noPaddingIcon
        />
        <Flex direction="column" alignItems="flex-start">
          <Flex direction="row" alignItems="center">
            <SkeletonRect isLoaded={!isLoading}>
              <Text textTransform="capitalize" mr="10px">
                {getWalletName({
                  wallet: walletResponse,
                  dataSource: walletResponse.dataSource,
                })}
              </Text>
              {statusProps.icon}
            </SkeletonRect>
          </Flex>
          <Flex>
            <SkeletonRect isLoaded={!isLoading}>
              <Text
                color={statusProps.color}
                pr="5px"
                fontSize="0.75rem"
                textTransform="uppercase"
              >
                {statusProps.status}
              </Text>
              <LastSyncedTime
                importMethod={walletResponse.importMethod}
                lastUpdated={statusProps.lastUpdated}
                whiteSpace="nowrap"
                fontSize="0.75rem"
              />
            </SkeletonRect>
          </Flex>
        </Flex>
      </Flex>

      {/* ------------ Transaction Number column -----------------*/}
      <Flex w="20%" minW="150px">
        <SkeletonRect isLoaded={!isLoading}>
          <Text
            whiteSpace="nowrap"
            color="red.500"
            fontWeight="500"
            cursor="pointer"
            onClick={() => history.push("/transactions")}
          >
            {walletResponse.transactionCount}
          </Text>
        </SkeletonRect>
      </Flex>
      {/* ------------ Token icons column -----------------*/}
      <Flex w="30%" minW="200px" alignItems="center">
        <RenderIcons
          assets={sortAssetHoldings(
            walletResponse.holdings ?? DUMMY_HOLDINGS
          ).map((holding) => holding.asset)}
          qty={3}
          isLoading={isLoading || !walletResponse.holdings}
        />
      </Flex>
      {/* ------------ Market Value column -----------------*/}
      <Flex w="20%" minW="150px" justifyContent="flex-end" pr="30px">
        <SkeletonRect isLoaded={!(isLoading || marketValueLoading)}>
          <NumberStyler
            num={walletValue}
            unit="currency"
            negativeColored
            mr="15px"
          />
        </SkeletonRect>
      </Flex>
    </Flex>
  );
};
